/**
 * Canonical Sazeo domain + local-port map.
 *
 * These values are locked by `specs/SAZEO_V2_ANATOMY.md` §2 and give every
 * app a single source of truth for URL construction instead of scattered
 * hardcoded hostnames.
 */

export const SAZEO_DOMAINS = {
  marketing: 'sazeo.io',
  adminApp: 'admin.sazeo.io', // platform admin — Sazeo engineers only (CF Access gated)
  teamApp: 'team.sazeo.io', // internal ops — Sazeo staff (leads, sales, inspections)
  intranet: 'app.sazeo.io', // tenant auth broker — /login /signup /reset /workspace-chooser
  tenantApi: 'app-api.sazeo.io',
  publicApi: 'api.sazeo.io',
  platformApi: 'platform-api.sazeo.io',
  iotIngest: 'ingest.sazeo.io', // IoT ingestion boundary (Phase 4)
} as const;

export const SAZEO_DEV_PORTS = {
  tenantApp: 3003,
  intranet: 3004,
  adminApp: 3005,
  marketing: 3006,
  publicApi: 8787,
} as const;

type UrlOptions = {
  isDev?: boolean;
  path?: string;
  protocol?: 'http' | 'https';
};

type TenantAppUrlOptions = UrlOptions & {
  devMode?: 'subdomain' | 'query-param';
};

function normalizePath(path: string): string {
  if (!path) return '/';
  return path.startsWith('/') ? path : `/${path}`;
}

export function buildTenantAppUrl(slug: string, options: TenantAppUrlOptions = {}): string {
  if (!slug) {
    throw new Error('[@sazeo/shared] buildTenantAppUrl requires a tenant slug.');
  }

  const isDev = options.isDev ?? false;
  const protocol = options.protocol ?? (isDev ? 'http' : 'https');
  const path = normalizePath(options.path ?? '/');

  if (isDev && options.devMode === 'query-param') {
    const url = new URL(`${protocol}://localhost:${SAZEO_DEV_PORTS.tenantApp}${path}`);
    url.searchParams.set('tenant', slug);
    return url.toString();
  }

  const host = isDev
    ? `${slug}.localhost:${SAZEO_DEV_PORTS.tenantApp}`
    : `${slug}.${SAZEO_DOMAINS.intranet}`;

  return `${protocol}://${host}${path}`;
}

export function buildIntranetUrl(path = '/', options: Omit<UrlOptions, 'path'> = {}): string {
  const isDev = options.isDev ?? false;
  const protocol = options.protocol ?? (isDev ? 'http' : 'https');
  const host = isDev ? `localhost:${SAZEO_DEV_PORTS.intranet}` : SAZEO_DOMAINS.intranet;

  return `${protocol}://${host}${normalizePath(path)}`;
}

export function buildAdminAppUrl(path = '/', options: Omit<UrlOptions, 'path'> = {}): string {
  const isDev = options.isDev ?? false;
  const protocol = options.protocol ?? (isDev ? 'http' : 'https');
  const host = isDev ? `localhost:${SAZEO_DEV_PORTS.adminApp}` : SAZEO_DOMAINS.adminApp;

  return `${protocol}://${host}${normalizePath(path)}`;
}
